// Design agent: Spec 009 — gives the method-2 hierarchical slider a fullscreen "zoom" mode so an
// operator carving many layers gets more room to see and adjust boundaries than the compact card
// allows, without duplicating any of VlsmCalculator's state: every prop this component receives is
// the exact same state/handler VlsmCalculator already passes to the inline slider and result card,
// so editing here and editing inline always agree.
// Developer agent: Rendered through a portal to `document.body` (the same pattern IpInspector.js
// already uses for its belonging-arrows overlay) so `position: fixed` is never at the mercy of an
// ancestor establishing its own containing block (e.g. a `transform` on a hovered `.tool-card`).
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { intToIpv4 } from '../utils/ipMath';
import HierarchicalSlider from './HierarchicalSlider';
import './SliderZoomOverlay.css';

// Design agent: Mirrors the layer palette used by VlsmCalculator's own layer breakdown list so a
// given layer's colour chip reads identically whether seen inline or inside the zoom overlay.
const LAYER_COLORS = [
  'var(--layer-color-a)',
  'var(--layer-color-b)',
  'var(--layer-color-c)',
  'var(--layer-color-d)',
];

// Design agent: Renders the fullscreen zoom view: the same slider component at full width, the
// per-layer rename fields on the left, and the address format / address explorer on the right, so
// zooming in genuinely gives more room to both see and edit the plan rather than only enlarging
// the slider itself.
function SliderZoomOverlay({
  id,
  isOpen,
  onClose,
  sliderProps,
  layers,
  onLayerLabelChange,
  onLayerLabelBlur,
  planError,
  planPending,
  addressFormulaRows,
  addressFormulaFontSize,
  layerExampleInputs,
  onExampleLayerChange,
  exampleHostIndex,
  onExampleHostChange,
  exampleAnalysis,
  exampleDisplaySegments,
  hostAddresses,
}) {
  // Design agent: Escape closes the overlay from anywhere, matching the acceptance criteria and the
  // conventional behaviour of every other fullscreen/modal surface.
  // Developer agent: The listener is only attached while `isOpen` is true, so it never competes
  // with the Alt+1/Alt+2/Alt+M shortcut listener VlsmCalculator.js keeps mounted at all times.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="slider-zoom-overlay" id={id} role="dialog" aria-modal="true" aria-label="Layer distribution — zoom view">
      <div className="slider-zoom-panel" id={`${id}-panel`}>
        <div className="slider-zoom-header">
          <span className="slider-zoom-title" id={`${id}-title`}>
            Layer distribution — zoom
          </span>
          <button
            type="button"
            className="ghost-button slider-zoom-close-button"
            id={`${id}-close-button`}
            onClick={onClose}
            aria-label="Close zoom view"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <div className="slider-zoom-slider-mount" id={`${id}-slider-mount`}>
          <HierarchicalSlider variant="zoom" {...sliderProps} />
        </div>

        {planError ? (
          <p className="error-text" id={`${id}-error`}>
            {planError}
          </p>
        ) : planPending ? (
          <p className="result-summary" id={`${id}-pending`}>
            Select a supernet, then drag the slider above to carve your addressing layers.
          </p>
        ) : (
          <div className="slider-zoom-columns" id={`${id}-columns`}>
            <div className="slider-zoom-column" id={`${id}-layer-fields`}>
              <div className="result-subheading">Layer fields</div>
              <ul className="result-list compact">
                {layers.map((layer) => {
                  const layerColour = LAYER_COLORS[layer.index % LAYER_COLORS.length];
                  const layerLabelValue = layer.label ?? '';
                  const trimmedLayerLabel = layerLabelValue.trim();
                  const fallbackLayerLabel = `Layer ${layer.index + 1}`;
                  const readableLayerLabel = trimmedLayerLabel === '' ? fallbackLayerLabel : trimmedLayerLabel;
                  return (
                    <li key={layer.id} className="result-item layer-result-item">
                      <div className="layer-result-header">
                        <span className="layer-colour-chip" style={{ backgroundColor: layerColour }} />
                        <input
                          type="text"
                          value={layerLabelValue}
                          onChange={(event) => onLayerLabelChange(layer.id, event.target.value)}
                          onBlur={() => onLayerLabelBlur(layer.id)}
                          className="result-title result-title-input"
                          aria-label={`Rename ${readableLayerLabel}`}
                          placeholder={fallbackLayerLabel}
                          autoComplete="off"
                        />
                        <span className={`readable-tag ${layer.isReadable ? 'is-readable' : 'is-unreadable'}`}>
                          Readable: {layer.isReadable ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="slider-zoom-column" id={`${id}-example`}>
              <div className="result-subheading result-subheading--formula">Address format</div>
              {addressFormulaRows.length === 0 ? (
                <p className="result-meta muted formula-placeholder">Adjust the slider to generate a readable formula.</p>
              ) : (
                <div
                  className="cidr-display cidr-display--formula"
                  aria-label="Address format"
                  style={{ '--formula-font-size': addressFormulaFontSize }}
                >
                  <span className="cidr-prefix">Formula</span>
                  <div className="cidr-octets">
                    {addressFormulaRows.map((row, rowIndex) => (
                      <span key={`zoom-formula-row-${rowIndex}`} className="cidr-row">
                        {row.map((segment) => (
                          <span key={`zoom-${segment.key}`} className="cidr-segment" style={segment.style}>
                            {segment.value}
                          </span>
                        ))}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="result-subheading">Address explorer</div>
              <div className="ip-examples-grid">
                {layers.map((layer, index) => {
                  const value = layerExampleInputs[index] ?? '';
                  const displayLabel = layer.label && layer.label.trim() !== '' ? layer.label.trim() : `Layer ${index + 1}`;
                  return (
                    <label key={layer.id} className="field ip-examples-field">
                      <span>
                        {displayLabel} network{' '}
                        <span
                          className="layer-colour-chip inline"
                          style={{ backgroundColor: LAYER_COLORS[index % LAYER_COLORS.length] }}
                        />
                      </span>
                      <input
                        value={value}
                        onChange={(event) => onExampleLayerChange(index, event.target.value)}
                        placeholder="e.g. 5"
                        className="field-input"
                        inputMode="numeric"
                        autoComplete="off"
                      />
                      <span className="field-hint">Up to {layer.networkCount.toLocaleString()} networks</span>
                    </label>
                  );
                })}
                <label className="field ip-examples-field">
                  <span>Host index</span>
                  <input
                    value={exampleHostIndex}
                    onChange={(event) => onExampleHostChange(event.target.value)}
                    placeholder="e.g. 23"
                    className="field-input"
                    inputMode="numeric"
                    autoComplete="off"
                  />
                  <span className="field-hint">Up to {hostAddresses.toLocaleString()} addresses</span>
                </label>
              </div>

              {exampleAnalysis && (
                <div className="ip-examples-result">
                  <div className="cidr-display" aria-label="Resolved example address">
                    <span className="cidr-prefix">Result</span>
                    <span className="cidr-octets">
                      {exampleDisplaySegments.map((segment) => (
                        <span
                          key={`zoom-${segment.key}`}
                          className="cidr-segment"
                          style={segment.style}
                          data-role={segment.role}
                        >
                          {segment.value}
                        </span>
                      ))}
                    </span>
                    <span className="cidr-separator">/</span>
                    <span className="cidr-segment" style={{ color: LAYER_COLORS[0] }}>
                      {exampleAnalysis.networkPrefix}
                    </span>
                  </div>
                  <p className="result-meta">
                    Host #{exampleAnalysis.hostIndex.toLocaleString()} within network{' '}
                    {intToIpv4(exampleAnalysis.networkAddress)}/{exampleAnalysis.networkPrefix}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default SliderZoomOverlay;
// Design agent: Also export named for import flexibility and IDE refactors, matching the sibling
// components in this directory (see HierarchicalSlider.js, PoolUsage.js).
export { SliderZoomOverlay };
